import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, X } from 'lucide-react';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * 新增禁撥號碼的 modal。
 *
 * jacob 的要求是「要顯示使用者要輸入的資訊」—— 所以欄位不是兩個光禿禿的 input，
 * 每一欄都寫清楚可以輸入什麼、會發生什麼：
 *   - 號碼格式：本地 / E.164 / 帶連字號都可以，**系統會自動正規化成同一個鍵**
 *     （這正是「+886912… 存進去、0912… 查不到」那種靜默失效的防線，使用者不必自己記格式）
 *   - 備註選填，但它是日後「為什麼擋這支」的唯一線索
 *   - 加入後**所有外撥路徑**（排程外撥／快速外撥／API 直撥）立刻都不會撥
 *
 * 樣式沿用後台既有 modal 慣例（同 ConfirmDeleteDialog / DeleteAvatarDialog）。
 */
export function AddPhoneDialog({ open, onClose, onSubmit, busy = false, error = null }) {
    const { t } = useTranslation('outbound');
    const [phone, setPhone] = useState('');
    const [note, setNote] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPhone(''); setNote('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    if (!open) return null;

    const canSubmit = phone.trim().length > 0 && !busy;
    const submit = (e) => { e.preventDefault(); if (canSubmit) onSubmit(phone.trim(), note.trim()); };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <form
                onSubmit={submit}
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2">
                        <Ban size={18} strokeWidth={1.8} className="text-accent-500" />
                        <h2 className="heading-display text-lg font-semibold text-ink-900">{t('bl.addPhone')}</h2>
                    </div>
                    <button
                        type="button" onClick={onClose} disabled={busy}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                            {t('bl.phone')}
                        </label>
                        <input
                            ref={inputRef}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={busy}
                            placeholder="0912345678"
                            autoComplete="off"
                            className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono disabled:bg-cream-100"
                        />
                        <p className="text-[11px] text-ink-400 mt-1.5 leading-relaxed">{t('bl.addPhoneHelp')}</p>
                    </div>

                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                            {t('bl.noteOptional')}
                        </label>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={busy}
                            placeholder={t('bl.noteHint')}
                            autoComplete="off"
                            className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white disabled:bg-cream-100"
                        />
                        <p className="text-[11px] text-ink-400 mt-1.5 leading-relaxed">{t('bl.addNoteHelp')}</p>
                    </div>

                    <div className="rounded-md bg-accent-50 border border-accent-300/50 px-3 py-2 text-[11.5px] text-ink-700 leading-relaxed">
                        {t('bl.addEffect')}
                    </div>

                    {error && (
                        <div className="text-sm text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded-md px-3 py-2">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        type="button" onClick={onClose} disabled={busy}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        {t('bl.cancel')}
                    </button>
                    <button
                        type="submit" disabled={!canSubmit}
                        className="px-4 py-2 text-sm rounded-md bg-accent-500 text-white hover:bg-accent-500/90 transition disabled:bg-cream-300 disabled:text-ink-400 disabled:cursor-not-allowed"
                    >
                        {busy ? t('bl.confirm.deleting') : t('bl.add')}
                    </button>
                </div>
            </form>
        </div>
    );
}
